// src/components/TooltipManager.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, MapPin, X } from 'lucide-react';
import { getCookie } from '../utils/utils';

/** Generate a stable CSS selector for an element (prefer id, then data-tooltip-anchor, then path). */
export function getSelectorForElement(el: Element): string {
  if (el.id && /^[a-zA-Z][\w-]*$/.test(el.id)) return `#${el.id}`;
  const anchor = el.getAttribute('data-tooltip-anchor');
  if (anchor) return `[data-tooltip-anchor="${CSS.escape(anchor)}"]`;
  const path: string[] = [];
  let current: Element | null = el;
  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();
    if (current.id && /^[a-zA-Z][\w-]*$/.test(current.id)) {
      path.unshift(`#${current.id}`);
      break;
    }
    const anchorAttr = current.getAttribute('data-tooltip-anchor');
    if (anchorAttr) {
      path.unshift(`[data-tooltip-anchor="${CSS.escape(anchorAttr)}"]`);
      break;
    }
    if (current.className && typeof current.className === 'string') {
      const classes = current.className.trim().split(/\s+/).filter(c => c && !/^(hover|focus|active):/.test(c));
      if (classes.length > 0 && classes.length <= 3) {
        const safe = classes.slice(0, 2).map(c => CSS.escape(c)).join('.');
        if (safe) selector += '.' + safe;
      }
    }
    const parent: Element | null = current.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter(c => c.tagName === current!.tagName);
      if (siblings.length > 1) {
        const idx = siblings.indexOf(current as Element) + 1;
        selector += `:nth-of-type(${idx})`;
      }
    }
    path.unshift(selector);
    current = parent;
  }
  return path.length ? path.join(' > ') : 'body';
}

export const PAGE_KEYS = [
  { key: 'project-list', label: 'Project list' },
  { key: 'blueprint-builder', label: 'Blueprint builder' },
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'outcomes', label: 'GOs' },
  { key: 'profile', label: 'Profile' },
  { key: 'organization', label: 'Organization' },
] as const;

const ANCHOR_OPTIONS = [
  { value: 'top-left', label: 'Top left' },
  { value: 'top-center', label: 'Top center' },
  { value: 'top-right', label: 'Top right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'bottom-center', label: 'Bottom center' },
  { value: 'bottom-right', label: 'Bottom right' },
  { value: 'center-left', label: 'Center left' },
  { value: 'center', label: 'Center' },
  { value: 'center-right', label: 'Center right' },
];

const ANCHOR_PLACEMENT_OPTIONS = [
  { value: 'top', label: 'Above element' },
  { value: 'bottom', label: 'Below element' },
  { value: 'left', label: 'Left of element' },
  { value: 'right', label: 'Right of element' },
];

export interface PlacedTooltipRecord {
  id: number;
  page_key: string;
  content: string;
  x_percent: number;
  y_percent: number;
  anchor: string;
  anchor_selector?: string | null;
  anchor_placement?: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const API_BASE = '/api/placed-tooltips';

function getCsrfHeaders(): Record<string, string> {
  const csrf = getCookie('csrftoken');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) headers['X-CSRFToken'] = csrf;
  return headers;
}

interface TooltipManagerProps {
  onClose?: () => void;
}

const TooltipManager: React.FC<TooltipManagerProps> = ({ onClose }) => {
  const [selectedPageKey, setSelectedPageKey] = useState<string>(PAGE_KEYS[0].key);
  const [tooltips, setTooltips] = useState<PlacedTooltipRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [waitingForClick, setWaitingForClick] = useState(false);
  const [form, setForm] = useState<Partial<PlacedTooltipRecord> & { page_key: string }>({
    page_key: PAGE_KEYS[0].key,
    content: '',
    x_percent: 50,
    y_percent: 50,
    anchor: 'top-left',
    anchor_selector: undefined,
    anchor_placement: 'bottom',
    display_order: 0,
    is_active: true,
  });

  const fetchTooltips = useCallback(async (pageKey: string) => {
    if (!pageKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/?page_key=${encodeURIComponent(pageKey)}`, { credentials: 'include' });
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setTooltips(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tooltips');
      setTooltips([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTooltips(selectedPageKey);
  }, [selectedPageKey, fetchTooltips]);

  const handlePageKeyChange = (pageKey: string) => {
    setSelectedPageKey(pageKey);
    setForm((f) => ({ ...f, page_key: pageKey }));
    setEditingId(null);
    setWaitingForClick(false);
  };

  // When "Click to place" is active, capture the next click on the page (outside this panel) and use that element as the anchor
  useEffect(() => {
    if (!waitingForClick) return;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (document.querySelector('[data-tooltip-manager-panel]')?.contains(target)) return;
      if ((target as Element).closest?.('button[title="Manage tooltips"]')) return;
      const el = target as Element;
      if (!el || !document.body.contains(el)) return;
      const selector = getSelectorForElement(el);
      setForm((f) => ({
        ...f,
        page_key: selectedPageKey,
        content: '',
        x_percent: 50,
        y_percent: 50,
        anchor: 'top-left',
        anchor_selector: selector,
        anchor_placement: 'bottom',
        display_order: tooltips.length,
        is_active: true,
      }));
      setWaitingForClick(false);
      setEditingId(-1);
    };
    document.addEventListener('click', handleDocClick, true);
    return () => document.removeEventListener('click', handleDocClick, true);
  }, [waitingForClick, selectedPageKey, tooltips.length]);

  const startClickToPlace = () => {
    setWaitingForClick(true);
    setEditingId(-1);
  };

  const openAdd = () => {
    setForm({
      page_key: selectedPageKey,
      content: '',
      x_percent: 50,
      y_percent: 50,
      anchor: 'top-left',
      anchor_selector: undefined,
      anchor_placement: 'bottom',
      display_order: tooltips.length,
      is_active: true,
    });
    setEditingId(-1);
    setWaitingForClick(false);
  };

  const openEdit = (t: PlacedTooltipRecord) => {
    setForm({
      page_key: t.page_key,
      content: t.content,
      x_percent: t.x_percent,
      y_percent: t.y_percent,
      anchor: t.anchor,
      anchor_selector: t.anchor_selector ?? undefined,
      anchor_placement: t.anchor_placement ?? 'bottom',
      display_order: t.display_order,
      is_active: t.is_active,
    });
    setEditingId(t.id);
    setWaitingForClick(false);
  };

  const closeForm = () => {
    setEditingId(null);
  };

  const saveTooltip = async () => {
    const payload = {
      page_key: form.page_key,
      content: form.content || '',
      x_percent: form.x_percent ?? 50,
      y_percent: form.y_percent ?? 50,
      anchor: form.anchor || 'top-left',
      anchor_selector: form.anchor_selector || null,
      anchor_placement: form.anchor_placement || 'bottom',
      display_order: form.display_order ?? 0,
      is_active: form.is_active ?? true,
    };
    setError(null);
    try {
      if (editingId != null && editingId > 0) {
        const res = await fetch(`${API_BASE}/${editingId}/`, {
          method: 'PATCH',
          credentials: 'include',
          headers: getCsrfHeaders(),
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await res.text());
      } else {
        const res = await fetch(API_BASE + '/', {
          method: 'POST',
          credentials: 'include',
          headers: getCsrfHeaders(),
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await res.text());
      }
      closeForm();
      fetchTooltips(selectedPageKey);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const deleteTooltip = async (id: number) => {
    if (!window.confirm('Delete this tooltip?')) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/${id}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getCsrfHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      if (editingId === id) closeForm();
      fetchTooltips(selectedPageKey);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const isFormOpen = editingId !== null;

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tooltip placement</h1>
          <p className="text-gray-600 text-sm mt-1 mb-6">
            Place and edit tooltips per page. Positions are stored as percentages so they stay consistent across resolutions.
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <label className="font-medium text-gray-700">Page</label>
        <select
          value={selectedPageKey}
          onChange={(e) => handlePageKeyChange(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          {PAGE_KEYS.map(({ key, label }) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={startClickToPlace}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${waitingForClick ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          <MapPin className="w-4 h-4" />
          {waitingForClick ? 'Click an element on the page…' : 'Click to place next to element'}
        </button>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
        >
          <Plus className="w-4 h-4" />
          Add tooltip
        </button>
      </div>

      {waitingForClick && (
        <p className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
          Click the element on the page (left side) you want the tooltip to appear next to.
        </p>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm">{error}</div>
      )}

      {isFormOpen && (
        <div className="mb-6 p-6 rounded-xl border border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-900 mb-4">{editingId > 0 ? 'Edit tooltip' : 'New tooltip'}</h2>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                value={form.content ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Tooltip text..."
              />
            </div>
            {(form.anchor_selector != null && form.anchor_selector !== '') ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Attached to element</label>
                <p className="text-xs text-gray-600 font-mono bg-white border border-gray-200 rounded px-2 py-1.5 truncate" title={form.anchor_selector}>
                  {form.anchor_selector}
                </p>
                <div className="flex items-center gap-2">
                  <select
                    value={form.anchor_placement ?? 'bottom'}
                    onChange={(e) => setForm((f) => ({ ...f, anchor_placement: e.target.value }))}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    {ANCHOR_PLACEMENT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, anchor_selector: undefined, anchor_placement: 'bottom' }))}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear (use position %)
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">X % (0–100)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={form.x_percent ?? 50}
                      onChange={(e) => setForm((f) => ({ ...f, x_percent: parseFloat(e.target.value) || 0 }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Y % (0–100)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={form.y_percent ?? 50}
                      onChange={(e) => setForm((f) => ({ ...f, y_percent: parseFloat(e.target.value) || 0 }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anchor (for % position)</label>
                  <select
                    value={form.anchor ?? 'top-left'}
                    onChange={(e) => setForm((f) => ({ ...f, anchor: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    {ANCHOR_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  value={form.display_order ?? 0}
                  onChange={(e) => setForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))}
                  className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <span className="text-sm text-gray-700">Display order</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active ?? true}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={saveTooltip}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
            >
              Save
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div>
        <h2 className="font-semibold text-gray-900 mb-3">Tooltips on this page</h2>
        {loading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : tooltips.length === 0 ? (
          <p className="text-gray-500 text-sm">No tooltips yet. Add one or use “Click to place” then fill the form.</p>
        ) : (
          <ul className="space-y-3">
            {tooltips.map((t) => (
              <li
                key={t.id}
                className="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 bg-white"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900 truncate">{t.content || '(empty)'}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t.anchor_selector
                      ? `Next to element · ${t.anchor_placement ?? 'bottom'}`
                      : `(${t.x_percent}%, ${t.y_percent}%) · ${t.anchor}`}
                    {!t.is_active && ' · Inactive'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(t)}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                    aria-label="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteTooltip(t.id)}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TooltipManager;
